import { useState } from 'react';

import { Typography } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import CloseableDrawer from '~components/drawers/closeableDrawer';
import InputField from '~components/inputFields/InputField';
import Switch from '~components/inputFields/switch';
import VectorImage from '~components/media/vectorImage';
import TooltipStyle from '~components/tooltips/enums/tooltipStyle';
import Tooltip from '~components/tooltips/tooltip';
import useColumnVisibility from '~hooks/useColumnVisibility';
import { Dimens } from '~themes';

import { ColDef } from '../interfaces/colDef';

interface Props<T> {
	isOpen: boolean;
	columns: ColDef<T>[];
	onClose: () => void;
}

function ColumnFilterDrawer<T>({ isOpen, columns, onClose }: Props<T>) {
	const { t } = useTranslation();
	const { palette } = useTheme();
	const columnVisibility = useColumnVisibility();
	const [searchInput, setSearchInput] = useState<string>('');

	const resetDefaults = () => {
		columns.forEach((column) => {
			columnVisibility.update(column.field, column.defaultShown);
		});
	};

	const enableAll = () => {
		columns.forEach((column) => {
			columnVisibility.update(column.field, true);
		});
	};

	const applySearchFilter = (column: ColDef<T>) => {
		return (
			column.field.toLowerCase().includes(searchInput.toLowerCase()) ||
			t(column.headerName).toLowerCase().includes(searchInput.toLowerCase())
		);
	};

	const closeDrawer = () => {
		setSearchInput('');
		onClose();
	};

	return (
		<CloseableDrawer
			title={t('manageColumns', { ns: 'table' })}
			description={t('manageColumnsDescription', { ns: 'table' })}
			open={isOpen}
			anchor='right'
			onClose={closeDrawer}
			sx={{ height: '100dvh' }}
			headerContent={
				<Stack gap={Dimens.spacingSm} sx={{ width: '100%' }}>
					<Stack direction='row' alignItems='center' sx={{ width: '100%' }} gap={Dimens.spacingMd}>
						<Box sx={{ flex: 1 }}>
							<InputField
								key={'search'}
								value={searchInput}
								placeholder={t('search', { ns: 'actions' })}
								StartIcon='icon/search'
								onChange={(newValue) => setSearchInput(newValue)}
							/>
						</Box>
						<Tooltip
							style={TooltipStyle.Compact}
							containerSx={{ width: 'fit-content' }}
							placement='top'
							content={
								<Typography variant='labelSm' color={palette.textWhite.main} component='span'>
									{t('resetToDefaults', { ns: 'actions' })}
								</Typography>
							}
						>
							<ActionButton
								onClick={resetDefaults}
								style={ActionButtonStyle.Text}
								sx={{ height: 'fit-content' }}
								startIcon={
									<VectorImage
										name='icon/refresh'
										color={palette.borderPrimarySolid.main}
										size={Dimens.spacing2xl}
									/>
								}
							/>
						</Tooltip>
					</Stack>
					<ActionButton
						label={t('selectAll', { ns: 'actions' })}
						onClick={enableAll}
						style={ActionButtonStyle.Text}
					/>
				</Stack>
			}
			footerContent={
				<ActionButton
					label={t('close', { ns: 'actions' })}
					sx={{ width: '100%' }}
					onClick={closeDrawer}
				/>
			}
			slotProps={{
				paper: {
					sx: {
						// Makes the drawer full screen on smaller devices
						left: {
							xs: 0,
							md: 'unset',
						},
					},
				},
			}}
		>
			<Stack gap={Dimens.spacingXl}>
				{columns.filter(applySearchFilter).map((column) => {
					const show = columnVisibility.isShown(column.field, column.defaultShown);
					return (
						<Switch
							key={column.field}
							isOn={show}
							onToggled={(newValue) => {
								columnVisibility.update(column.field, newValue);
							}}
							title={t(column.headerName)}
							subtitle={column.field}
							sx={{ width: '100%' }}
						/>
					);
				})}
			</Stack>
		</CloseableDrawer>
	);
}

export default ColumnFilterDrawer;
